import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { ProductionRoute } from '@/features/production-routes/api/ProductionRouteModel'
import {
  EmissionReport,
  MethodEmissionReport,
  MethodEmissionReportPatch,
  EmissionReportPatchPayload
} from '@/api/EmissionReportModel'
import { INSTALLATIONS_QUERY_KEY } from '@/features/installations/hooks/useInstallations'
import { ReportCalculatedEmissions } from '@/shared/api/types'
import { PrecursorMaterialWithSupplier } from '@/api/PrecursorMaterialModel'
import { PreconsumerScrapMaterial } from '@/api/PreconsumerScrapMaterialModel'
import { useCallback } from 'react'

export const EMISSION_REPORTS_QUERY_KEY = 'emission-reports'
const METHOD_EMISSION_REPORTS_QUERY_KEY = 'method-emission-reports'

type EmissionReportWithProductionRoute = EmissionReport & {
  productionRoute: ProductionRoute
  methodEmissionReports: MethodEmissionReport[]
  precursorMaterials: PrecursorMaterialWithSupplier[]
  preconsumerScrapMaterials: PreconsumerScrapMaterial[]
}

export const useReEvaluateActivityLevelValidationForMethodEmissionReport = (
  emissionReportId: string,
  methodEmissionReportId: string | null
) => {
  const { data: methodEmissionReports, isLoading: isLoadingMethodEmissionReports } =
    useMethodEmissionReports(emissionReportId)
  const { mutateAsync: updateMethodEmissionReport, isPending: isUpdatingMethodEmissionReport } =
    useUpdateMethodEmissionReport(emissionReportId, methodEmissionReportId)

  const methodEmissionReport = methodEmissionReports?.find(
    methodEmissionReport => methodEmissionReport.id === methodEmissionReportId
  )

  const reEvaluateActivityLevelValidationForMethodEmissionReport = useCallback(async () => {
    if (methodEmissionReport && methodEmissionReport.activityLevelValidation) {
      await updateMethodEmissionReport({
        activityLevelValidation: {
          ...methodEmissionReport.activityLevelValidation,
          hasToBeReEvaluated: true
        }
      })
    }
  }, [methodEmissionReport, updateMethodEmissionReport])

  return {
    mutateAsync: reEvaluateActivityLevelValidationForMethodEmissionReport,
    isPending: isLoadingMethodEmissionReports || isUpdatingMethodEmissionReport
  }
}

export const useEmissionReport = (emissionReportId: string) => {
  return useQuery<EmissionReportWithProductionRoute>({
    queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId],
    queryFn: async () => {
      const res = await apiClient.get<EmissionReportWithProductionRoute>(
        `/v1/emission-reports/${emissionReportId}`
      )
      return res.data
    },
    enabled: !!emissionReportId
  })
}

export const useUpdateEmissionReport = (emissionReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: EmissionReportPatchPayload) => {
      const res = await apiClient.patch<EmissionReport>(
        `/v1/emission-reports/${emissionReportId}`,
        payload
      )

      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId]
      })

      return res.data
    }
  })
}

export const useSubmitEmissionReport = (emissionReportId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.post(`/v1/emission-reports/${emissionReportId}/submit`)

      queryClient.refetchQueries({
        queryKey: [INSTALLATIONS_QUERY_KEY]
      })

      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY]
      })
    }
  })
}

export const useEmissionsReportCalculatedEmissions = (emissionReportId: string) => {
  return useQuery<ReportCalculatedEmissions>({
    queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId, 'emissions'],
    queryFn: async () => {
      const res = await apiClient.get<ReportCalculatedEmissions>(
        `/v1/emission-reports/${emissionReportId}/emissions`
      )
      return res.data
    },
    enabled: !!emissionReportId
  })
}

export const useMethodEmissionReports = (emissionReportId: string) => {
  return useQuery<MethodEmissionReport[]>({
    queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId, METHOD_EMISSION_REPORTS_QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<MethodEmissionReport[]>(
        `/v1/emission-reports/${emissionReportId}/method-emission-reports`
      )
      return res.data
    },
    enabled: !!emissionReportId
  })
}

export const useUpdateMethodEmissionReport = (
  emissionReportId: string,
  methodEmissionReportId: string | null
) => {
  const queryClient = useQueryClient()
  return useMutation<MethodEmissionReportPatch, Error, MethodEmissionReportPatch>({
    mutationFn: async (payload: MethodEmissionReportPatch) => {
      if (!methodEmissionReportId) {
        throw new Error('Method emission report ID is required')
      }
      const res = await apiClient.patch<MethodEmissionReport>(
        `/v1/emission-reports/${emissionReportId}/method-emission-reports/${methodEmissionReportId}`,
        payload
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId, METHOD_EMISSION_REPORTS_QUERY_KEY]
      })
      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId]
      })
    }
  })
}
